// Roles included in LLM context. human_agent is mapped to "assistant" at call time.
export const LLM_ROLES = ["user", "assistant", "human_agent"];
// The four columns every client keeps, locked (no rename/delete/reorder). Their
// keys are stable and the score→stage auto-advance depends on them.
export const FIXED_STAGE_KEYS = ["new", "qualifying", "warm", "hot"];
// What a pipeline column MEANS to reporting, as opposed to what it is called.
// Before this existed, "converted" was the literal key `enrolled` — a seeded but
// NOT fixed column, so renaming it (which the pilot did) silently repurposed every
// conversion metric, and the digest started chasing leads that had already closed.
// `conversion` is exclusive per client; `lost` may mark several columns.
export const STAGE_ROLES = ["conversion", "lost"];
// Default widget primary color when a client has not configured one — the
// ScholaOps moss green (oklch(0.30 0.05 145) ≈ #1d341e). Kept in sync with the
// widget CSS fallback in apps/widget/src/index.css.
export const DEFAULT_WIDGET_PRIMARY_COLOR = "#1d341e";
// ----- Orders (cart + pedidos) -----
/**
 * An order's outcome, not its activity. Which order is current is
 * `leads.active_order_id`.
 *
 * A plain union over a VarChar column rather than a Prisma enum, for the reason
 * the migration header gives: a commercial lifecycle grows, and a new state must
 * not cost a type migration. There is no `paid` — with cash on delivery, payment
 * and delivery are one event in the field and we never see the money.
 */
export const ORDER_STATUSES = [
    /** The cart. */
    "open",
    /** Confirmed with the customer; awaiting delivery. */
    "confirmed",
    /** Handed over and paid in cash. Terminal — a return is a different transaction. */
    "delivered",
    "cancelled",
    /** An `open` cart that aged out. Nothing writes it yet. */
    "abandoned",
];
/** The statuses a human may still act on from the CRM. */
export const ACTIONABLE_ORDER_STATUSES = ["open", "confirmed"];
/**
 * Longest delivery address we store.
 *
 * In shared, not beside the tool that first needed it, for the reason
 * `TEMPLATE_MEDIA_LIMITS` is: the agent tool, the CRM form and the route schema
 * must all enforce the SAME number, and three copies drift. Generous enough for
 * a Mexican address with references ("entre X y Y, portón verde"), short enough
 * that it still renders in a table cell.
 */
export const MAX_DELIVERY_ADDRESS_LENGTH = 300;
/** How a custom column is addressed wherever a status could be: `stage:<id>`. */
export const ORDER_BOARD_STAGE_PREFIX = "stage:";
/** The column an order sits in. One definition, so the board and the filters agree. */
export function orderColumnKey(order) {
    return order.status === "confirmed" && order.boardStageId
        ? `${ORDER_BOARD_STAGE_PREFIX}${order.boardStageId}`
        : order.status;
}
/** The custom column's id, or null when the key names a status. */
export function orderBoardStageIdFromKey(key) {
    return key.startsWith(ORDER_BOARD_STAGE_PREFIX)
        ? key.slice(ORDER_BOARD_STAGE_PREFIX.length)
        : null;
}
/** Who took a confirmed order: the agent, or someone on the team. */
export const ORDER_TAKEN_BY = ["agent", "team"];
/**
 * A quote request's lifecycle. A plain union over a VarChar + CHECK column, the
 * `orders.status` precedent: a new state must not cost a type migration.
 *
 * Everything after `submitted` is moved by a human, because the pricing happens
 * outside the system.
 */
export const QUOTE_STATUSES = [
    /** The agent is assembling it. */
    "draft",
    /** It reached the team; nobody has taken it yet. The agent may still edit it. */
    "submitted",
    /** Someone is pricing it. Frozen for the agent from here on. */
    "in_progress",
    /** The team sent its own quote (their PDF). */
    "sent",
    "won",
    "lost",
    /** A draft nobody submitted, closed when its chat session rotated. */
    "abandoned",
];
/** The statuses a request still sits in while somebody owes the customer something. */
export const IN_FLIGHT_QUOTE_STATUSES = [
    "submitted",
    "in_progress",
    "sent",
];
/**
 * The moves a human may make from the CRM, by current status. One table read by
 * the backend (which refuses anything else) and by the detail's buttons, so the
 * two cannot disagree. `draft` and `abandoned` are the agent's and the session
 * rotation's; a human never moves a request into or out of them.
 */
export const QUOTE_HUMAN_TRANSITIONS = {
    draft: [],
    submitted: ["in_progress", "sent", "lost"],
    in_progress: ["sent", "lost"],
    sent: ["won", "lost"],
    won: [],
    lost: [],
    abandoned: [],
};
/**
 * Bounds shared by the agent tool, the route schema and the form — one number
 * each, the `MAX_DELIVERY_ADDRESS_LENGTH` rule. B2B quantities are large (500
 * cable ties is an ordinary request), so the cart's 99 does not apply here.
 */
export const MAX_QUOTE_REFERENCE_LENGTH = 120;
export const MAX_QUOTE_LINE_NOTES_LENGTH = 300;
export const MAX_QUOTE_LINE_QUANTITY = 99_999;
export const MAX_QUOTE_LOST_REASON_LENGTH = 200;
/**
 * The outcome of checking one address against the zones.
 *
 * `covered` names the zone and its fee; `not_covered` means the point is
 * outside every zone; `ambiguous` means the address resolved to an AREA (a
 * colonia, a postal code, a street with no number) that straddles a zone border
 * or two fees — the honest answer is "need the street and number"; `not_found`
 * means the geocoder could not place it at all.
 */
export const COVERAGE_STATUSES = ["covered", "not_covered", "ambiguous", "not_found"];
// ----- Promotions -----
/**
 * How a promotion arrives at its price.
 *
 * `price_override` SETS the price for the window and is deliberately not
 * constrained below the list price: the case that motivated this feature is a
 * shop whose price for the same product differs by hour, and peak can be higher.
 * `percent_off` only ever reduces. That asymmetry is why this module is called
 * "promociones" and never "descuentos".
 *
 * A tuple, not a Zod enum, because `schemas/promotions.ts` builds its enum FROM
 * this — the same value-import rule `STAGE_ROLES` follows, so the DTO and the
 * validator cannot drift.
 */
export const PROMOTION_KINDS = ["price_override", "percent_off"];
export const PROMOTION_KIND_LABELS = {
    price_override: "Precio fijo",
    percent_off: "Descuento %",
};
/**
 * What the admin chose about telling the customer.
 *
 * Lives here rather than in JSX because it is the one setting whose wrong
 * reading has a cost outside the screen, and both the list and the editor say it.
 */
export function promotionVisibilityLabel(announce) {
    return announce ? "Menciona la promoción" : "Sólo el precio nuevo";
}
// ----- Plan features -----
/**
 * The gateable capabilities, as the `features.key` column spells them.
 *
 * The DB column stays free text — features are code-backed and a new key ships
 * with the code that gates it, never from the UI — so this is a typed READ side,
 * not a constraint on the table. It exists because a gate that asks for a key
 * nobody grants fails *open* by omission, and a typo in a string literal is
 * indistinguishable from a tenant who did not buy the feature: no error, no log,
 * just behaviour quietly present where it was meant to be absent.
 *
 * Keep in step with the `FEATURES` catalog in `apps/backend/prisma/seed.ts` and
 * `seed-plans.ts`, and with `MANAGEABLE_FEATURE_KEYS` in `plan.service.ts` for
 * any key the Planes editor should offer.
 */
export const FEATURE_KEYS = {
    visits: "visits",
    reports: "reports",
    documents: "documents",
    waba: "waba",
    institutionalImage: "institutional_image",
    whatsappTemplates: "whatsapp_templates",
    /** School-shaped behaviour: intake cycles, product calendars, candidates. */
    enrollment: "enrollment",
    /** Cart, order confirmation and (per-client) inventory. */
    orders: "orders",
    /**
     * Home delivery of orders: the delivery zones an admin draws, the agent's
     * address capture (`set_delivery_address` / `check_delivery_coverage`) and
     * every sentence that offers shipping. Without it a tenant with `orders` is
     * PICKUP ONLY — the agent never offers delivery nor asks for an address.
     *
     * Depends on `orders` (`FEATURE_DEPENDENCIES`): delivery is a property of an
     * order, so a plan cannot grant it alone. The Planes editor prevents it, the
     * API refuses it, and `planService` drops an orphan on read.
     */
    delivery: "delivery",
    /** Time-bounded pricing windows over chosen products. */
    promotions: "promotions",
    /**
     * The quote-request close: the agent assembles what the customer needs (product,
     * the reference they gave, quantity, notes) and hands it to the team, who price
     * it OUTSIDE the system. A tenant with it never shows the agent a price —
     * `catalogService.getContext` strips them at the source.
     *
     * Conflicts with `orders` (`FEATURE_CONFLICTS`): one close per tenant for now.
     */
    quotes: "quotes",
    /**
     * Per-visit conversation sessions: the agent's history is cut at the start of
     * the current session, so a customer who returns every few weeks does not drag
     * an unbounded transcript into the prompt. What survives a boundary is the
     * CUSTOMER — captured fields and previous orders — never the conversation.
     *
     * Independent of `orders` on purpose: the two answer different questions
     * (can this tenant take a cart / does this tenant's buyer come back), and
     * either has to be switchable without the other.
     */
    chatSessions: "chat_sessions",
    /**
     * The daily digest email. A delivery gate, not a prompt axis: it never enters
     * `enabledFeatures`, the system prompt or the toolset.
     *
     * It is the most expensive recurring thing we do per tenant that nobody was
     * ever charged for — one LLM narrative call plus nine aggregate queries plus a
     * SendGrid send, every morning, for every active client. An unentitled tenant
     * is skipped before any of it (`digest.service.ts`), so a downgrade actually
     * stops the spend rather than only hiding a screen.
     */
    dailyDigest: "daily_digest",
    /**
     * Lead assignment: a responsible team member per lead, the members-only
     * visibility that follows from it, round-robin auto-assignment and member
     * transfers (Agente → «Políticas de asignación de prospectos»).
     *
     * The plan grants the CAPABILITY; the school's admin still decides whether to
     * use it (`clients.lead_assignment_enabled`). Gated on READ: without the
     * feature the stored policy is treated as off everywhere — nobody is
     * restricted, nothing is auto-assigned — but nothing is deleted, so a school
     * that re-upgrades gets its configuration and its assignments back.
     */
    leadAssignment: "lead_assignment",
    /**
     * In-CRM notifications (bell, toasts) and browser Web Push for three events:
     * order confirmed, quote requested, lead handed to a human. The plan grants
     * the capability; the school's admin picks which events notify and whether
     * admins receive them (Admin → «Notificaciones»). A delivery gate like
     * `daily_digest`: it never enters `enabledFeatures`, the prompt or the toolset.
     */
    webNotifications: "web_notifications",
    /**
     * «Mensajes de seguimiento»: when the agent is waiting on a WhatsApp customer
     * who went quiet, it writes to them again — an AI-composed message that picks
     * up what was pending in that conversation — after a delay and up to a count
     * the school's admin sets (Agente → Ajustes). The plan grants the capability,
     * `clients.reengagement_enabled` is the school's own switch, and
     * `REENGAGEMENT_ENABLED` is the platform's.
     *
     * It DOES enter `enabledFeatures` (via `getAgentFeatureSet`), unlike the
     * delivery gates: a tenant with follow-ups gets the agent tool that stops them
     * (`stop_reengagement_messages`) and the prompt section that explains it,
     * because a customer who is being written to must always be able to say no.
     */
    reengagement: "reengagement",
    /**
     * «Sitio web»: the AI-first website (docs/plans/ai-first-site.md) — the agent
     * at the centre of a server-rendered page built from the tenant's own catalog.
     * It unlocks the «Sitio web» editor and the public read the site renders from
     * (`GET /public/sites/:host`), which answers 404 for a tenant without it.
     *
     * A delivery gate like `daily_digest` for now: it never enters
     * `enabledFeatures`, the prompt or the toolset. That changes when the rich
     * replies (product cards, the quote panel) ship behind it.
     */
    website: "website",
    /**
     * «Instagram»: the agent answers Instagram DMs (docs/INSTAGRAM_SETUP.md), the
     * team answers them from the CRM, and a person who writes on Instagram and on
     * WhatsApp becomes ONE lead (the automatic merge, docs/LEAD_MERGE.md — which
     * only exists because of this channel, so it hangs off the same key).
     *
     * A delivery gate like `daily_digest`: it never enters `enabledFeatures`, the
     * prompt or the toolset — the channel the turn is built for is decided by the
     * transport, not by the plan. No dependency on `waba`: Instagram is not
     * WhatsApp, and a Baileys tenant can have it.
     */
    instagram: "instagram",
};
const FEATURE_KEY_SET = new Set(Object.values(FEATURE_KEYS));
/**
 * Narrows a `features.key` read from the DB onto the union.
 *
 * A key this build does not know about is dropped rather than carried through:
 * nothing can gate on it, and keeping it would make the resulting set's type a
 * claim the data does not support. The reverse case — a key the code expects and
 * the DB lacks — is the dangerous one, and it is handled by seeding the row
 * before the gate ships, not here.
 */
export function isFeatureKey(value) {
    return FEATURE_KEY_SET.has(value);
}
/**
 * Features that only mean something on top of another one. A key whose
 * prerequisites are missing is not granted — never the other way round (a
 * prerequisite is never added implicitly), so a misconfigured plan renders LESS.
 *
 * One table read by the Planes editor (which keeps the checkboxes consistent),
 * by the plan API (which refuses an inconsistent plan) and by the feature
 * resolution (which drops an orphan a hand-written SQL could still create).
 */
export const FEATURE_DEPENDENCIES = {
    delivery: [FEATURE_KEYS.orders],
};
/** The prerequisites `key` lacks within `keys` (empty when it may be granted). */
export function missingFeatureDependencies(key, keys) {
    if (!isFeatureKey(key))
        return [];
    return (FEATURE_DEPENDENCIES[key] ?? []).filter((dep) => !keys.has(dep));
}
/**
 * Features that cannot be granted together. Symmetric by construction (read
 * through `conflictingFeatures`), so the table lists each pair once.
 *
 * `quotes` × `orders`: one close per tenant for now. Mixed tenants (priced
 * consumables + quoted engineering) are a later, per-product decision.
 */
export const FEATURE_CONFLICTS = {
    quotes: [FEATURE_KEYS.orders],
};
/** The keys in `keys` that `key` cannot be granted alongside (empty when none). */
export function conflictingFeatures(key, keys) {
    if (!isFeatureKey(key))
        return [];
    const out = new Set();
    for (const other of FEATURE_CONFLICTS[key] ?? [])
        if (keys.has(other))
            out.add(other);
    for (const [owner, others] of Object.entries(FEATURE_CONFLICTS)) {
        if (others?.includes(key) && keys.has(owner) && isFeatureKey(owner))
            out.add(owner);
    }
    return [...out];
}
/**
 * Which side of a conflicting pair a read DROPS when a plan grants both anyway
 * (a hand-written SQL can; the editor and the API cannot).
 *
 * `orders`, never `quotes`: the irreversible harm of this misconfiguration is a
 * quote tenant's agent reciting prices to a competitor, and dropping the cart
 * fails visibly instead — a shop with no cart is noticed the same day.
 */
const CONFLICT_LOSERS = new Set([FEATURE_KEYS.orders]);
/**
 * `keys` minus every key whose prerequisites are not all present, and minus the
 * losing side of every conflicting pair. Runs to a fixed point, so a chain
 * (c → b → a) collapses entirely when `a` is missing — including when `a` was
 * dropped by a conflict (`quotes` drops `orders`, which drops `delivery`).
 */
export function applyFeatureDependencies(keys) {
    const result = new Set(keys);
    for (const key of result) {
        if (CONFLICT_LOSERS.has(key) && conflictingFeatures(key, result).length > 0) {
            result.delete(key);
        }
    }
    let changed = true;
    while (changed) {
        changed = false;
        for (const key of result) {
            if (missingFeatureDependencies(key, result).length > 0) {
                result.delete(key);
                changed = true;
            }
        }
    }
    return result;
}
//# sourceMappingURL=index.js.map